import type { JSX, ReactNode } from 'react'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader
} from '../components/ui/card.tsx'
import { cn } from '../lib/cn.ts'

/**
 * How much horizontal room a panel takes.
 *
 * `form` is the default and is a measure cap, not decoration: a column of
 * fields wider than Tailwind's `2xl` container is hard to read and hard to
 * scan back along.
 *
 * `wide` fills the shell's `<main>`, which sets no width of its own. It is for
 * a panel whose content is a table — a fifteen-column report does not fit in a
 * form measure, and a `className` override would hide that from the next
 * screen that needs one. Choose it deliberately; most screens are `form`.
 */
const WIDTHS = {
  form: 'max-w-2xl',
  wide: 'max-w-none'
} as const satisfies Record<string, string>

export interface PanelProps {
  'data-testid': string
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  width?: keyof typeof WIDTHS
  className?: string
}

/**
 * Panel — the console's unit of routed content.
 *
 * Every screen is a titled card, so the heading level and spacing are decided
 * once here instead of per screen. Screens compose Panels; they do not reach
 * for `Card` directly.
 *
 * @param props - Panel props.
 * @param props.title - The panel heading.
 * @param props.description - Optional supporting line under the title.
 * @param props.children - Panel body.
 * @param props.width - How much horizontal room to take; see `WIDTHS`.
 * @param props.className - Extra classes.
 * @returns The rendered panel.
 */
export function Panel({
  'data-testid': testId,
  title,
  description,
  children,
  width = 'form',
  className
}: PanelProps): JSX.Element {
  return (
    <Card data-testid={testId} className={cn(WIDTHS[width], className)}>
      <CardHeader>
        <h1 className="text-2xl leading-none font-semibold">{title}</h1>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="grid gap-4">{children}</CardContent>
    </Card>
  )
}
